import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import clsx from 'clsx';

import { requestUserAuthTokenExchange } from '@atom/actions/userActions';
import { useNavigation } from '@atom/hooks/useNavigation';
import { useTenantConnections } from '@atom/hooks/useTenantConnections';
import { Icon, IconButton, Progress, TextField } from '@atom/mui';
import { RemoveRedEyeIcon, VisibilityOffIcon } from '@atom/mui/icons';
import { RootState } from '@atom/types/store';
import accountUtilities from '@atom/utilities/accountUtilities';

import AuthTopBar from './AuthTopBar';
import LoginFooter from './LoginFooter';
import ResetPasswordModal from './ResetPasswordModal';
import SsoButtonGroup from './SsoButtonGroup';

import classes from './login.module.css';

const styles = {
  passwordField: {
    paddingTop: '0.4rem',
  },
};

type LoginForm = {
  email: string;
  password: string;
};

const Login = () => {
  const dispatch = useDispatch();
  const isLoginLoading = useSelector(
    (state: RootState) => state.loading.loadingUserLogin,
  );
  const [, { navigate }] = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { connections, isLoading: isTenantLoading } = useTenantConnections();

  const [authError, setAuthError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [emailPasswordError, setEmailPasswordError] = useState<boolean>(false);

  // Show the SSO error once, then clear the param so it doesn't linger.
  useEffect(() => {
    if (searchParams.get('error') === 'auth_failed') {
      setAuthError(true);

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('error');
      setSearchParams(nextParams, { replace: true });
    }
  }, []);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setLoginForm(prevFormData => ({ ...prevFormData, [name]: value }));
  };

  const submit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await accountUtilities.individualAccountLogin(
        loginForm.email,
        loginForm.password,
      );

      dispatch(
        requestUserAuthTokenExchange({
          accessToken: result.accessToken,
        }),
      );
    } catch (error) {
      setEmailPasswordError(true);
      setIsSubmitting(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      submit();
    }
  };

  const navigateToRegistration = () => {
    navigate('/registration');
  };

  const togglePasswordVisibleState = () => {
    setIsPasswordVisible(prevIsVisible => !prevIsVisible);
  };

  const content =
    isTenantLoading || isLoginLoading ? (
      <div className={classes.spinnerContainer}>
        <Progress />
      </div>
    ) : (
      <>
        <div className={clsx(classes.authBackground, classes.complete)} />
        <div className={classes.headingText}>Sign in to Atom</div>
        {emailPasswordError && (
          <div className={classes.errorText}>Invalid email or password</div>
        )}
        {authError && (
          <div className={classes.errorText}>
            Authentication failed. Please try again.
          </div>
        )}
        <div className={classes.textFieldContainer}>
          <div className={classes.textField}>
            <TextField
              variant="standard"
              type="email"
              value={loginForm.email}
              name="email"
              label="Email"
              onChange={onChange}
              onKeyDown={onKeyDown}
            />
          </div>
          <div className={clsx(classes.textField, 'fs-mask')}>
            <TextField
              variant="standard"
              type={isPasswordVisible ? 'text' : 'password'}
              value={loginForm.password}
              name="password"
              label="Password"
              onChange={onChange}
              onKeyDown={onKeyDown}
              style={styles.passwordField}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton
                      onClick={togglePasswordVisibleState}
                      size="small"
                    >
                      <Icon
                        icon={
                          isPasswordVisible
                            ? VisibilityOffIcon
                            : RemoveRedEyeIcon
                        }
                      />
                    </IconButton>
                  ),
                },
              }}
            />
          </div>
        </div>
        <ResetPasswordModal />
        <div className={classes.genericButtonContainer}>
          <button
            className={classes.genericButton}
            onClick={submit}
            disabled={isSubmitting}
          >
            Sign In
          </button>
        </div>
        <SsoButtonGroup verb="Sign in" connections={connections} />
      </>
    );

  return (
    <>
      <AuthTopBar
        prompt="Don't have an account?"
        ctaLabel="Sign Up"
        onCtaClick={navigateToRegistration}
      />
      <div className={classes.container}>
        {content}
        <LoginFooter />
      </div>
    </>
  );
};

export default Login;
